import React from 'react';
import { Box, Drawer, Grid2, IconButton, Link, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Typography, useMediaQuery } from '@mui/material';
import BlackLogo from 'assets/logo/black.png';
import ClosedMenuLogo from 'assets/logo/closed-menu.png';
import { useLocation, useHistory } from 'react-router-dom';
import { ArrowBackIos, ArrowForwardIos, Close, ExitToApp, Menu } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StyledTooltip } from './styles';
import LanguageDropDown from 'components/LanguageDropDown';
import MoneyDropDown from 'components/MoneyDropDown';

const Sidebar = ({ open, toggleMenu = () => {}, sections, onLogoutClick }) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    const { t: translate } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const drawerWidth = isMobile ? '100vw' : 240;

    const drawerStyles = {
        width: drawerWidth,
        flexShrink: 0,
    };

    const drawerOpenStyles = {
        width: drawerWidth,
        transition: (theme) =>
            theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
    };

    const drawerCloseStyles = {
        transition: (theme) =>
            theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        overflowX: 'hidden',
        width: 64,
    };

    const renderIcon = ({ src, size = 24, color = '#454545', display = 'inline-block' }) => (
        <Box
            component="span"
            sx={{
                width: size,
                height: size,
                backgroundColor: color,
                mask: `url(${src}) no-repeat center`,
                WebkitMask: `url(${src}) no-repeat center`,
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                display,
            }}
        />
    );

    const getActualPath = (path) => {
        const locationPathname = location.pathname?.split('/')[1];
        return path?.split('/')[1] === locationPathname;
    };

    return (
        <>
            {isMobile && !open && (
                <IconButton
                    edge="start"
                    size="large"
                    onClick={toggleMenu}
                    sx={{
                        position: 'fixed',
                        zIndex: 1200,
                        boxShadow: 3,
                        height: '40px',
                        width: '40px',
                        backgroundColor: '#efefef',
                        borderRadius: '0 4px 4px 0',
                        top: '24px',
                        left: 0,
                        marginLeft: 0,
                        '&:hover': { backgroundColor: '#e0e0e0' },
                    }}>
                    <Menu sx={{ color: '#131313' }} />
                </IconButton>
            )}
            <Box sx={{ position: 'relative', display: 'flex' }}>
                {!open && !isMobile && (
                    <StyledTooltip title={translate('layout.menu.expand')} placement="right" disableHoverListener={open}>
                        <IconButton
                            onClick={toggleMenu}
                            sx={{
                                position: 'fixed',
                                left: 72,
                                top: '2em',
                                zIndex: 1201,
                                backgroundColor: '#efefef',
                                borderRadius: '0 4px 4px 0',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                },
                            }}>
                            <ArrowForwardIos sx={{ fontSize: '16px', color: '#131313' }} />
                        </IconButton>
                    </StyledTooltip>
                )}
                <Drawer
                    open={open}
                    variant={isMobile ? 'persistent' : 'permanent'}
                    ModalProps={{ keepMounted: true }}
                    anchor="left"
                    sx={drawerStyles}
                    slotProps={{
                        paper: {
                            sx: {
                                width: open ? drawerOpenStyles : drawerCloseStyles,
                                border: 'transparent',
                                backgroundColor: '#efefef',
                                boxShadow: 'rgba(0, 0, 0, 0.2) 0px 15px 30px',
                                margin: isMobile ? 0 : '1em',
                                borderRadius: isMobile ? 0 : '.5em',
                                height: '100dvh',
                                maxHeight: '-webkit-fill-available',
                                zIndex: 1200,
                            },
                        },
                    }}>
                    {/* Header */}
                    <Grid2 container justifyContent={{ xs: 'center', md: 'space-between' }} alignItems="center" padding={(theme) => theme.spacing(4, 2)} pb={2} sx={{ position: 'relative' }}>
                        <Box component="img" src={ClosedMenuLogo} alt="utriper-logo" sx={{ width: 32, height: 32, objectFit: 'contain', display: open ? 'none' : 'flex' }} />
                        <Box
                            component="img"
                            src={BlackLogo}
                            alt="utriper-logo"
                            sx={{
                                width: 'auto',
                                height: 32,
                                objectFit: 'contain',
                                marginRight: { xs: 'auto', md: 0 },
                                order: { xs: 2, md: 1 },
                                display: open ? 'flex' : 'none',
                            }}
                        />

                        {open && (
                            <IconButton
                                onClick={toggleMenu}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    order: { xs: 1, md: 2 },
                                    marginRight: { xs: 'auto', md: 0 },
                                }}>
                                {isMobile ? <Close sx={{ fontSize: '16px', color: '#131313' }} /> : <ArrowBackIos sx={{ fontSize: '16px', color: '#131313' }} />}
                            </IconButton>
                        )}
                    </Grid2>

                    {/* Sections */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            overflowY: 'auto',
                            position: 'relative',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#bbb',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent',
                            },
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#bbb transparent',
                        }}>
                        {sections?.map((section) => {
                            const sectionTitle = translate(section?.title);

                            return (
                                <List
                                    key={`section-${sectionTitle}`}
                                    sx={{ width: '100%', px: open ? '16px' : '8px' }}
                                    component="nav"
                                    aria-labelledby={`menu-list-${sectionTitle}`}
                                    subheader={
                                        <ListSubheader
                                            id={`menu-list-${sectionTitle}`}
                                            component="div"
                                            disableSticky
                                            sx={{
                                                backgroundColor: '#efefef',
                                                padding: 0,
                                                maxHeight: '2em',
                                                color: '#7F7F7F',
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                justifyContent: open ? 'flex-start' : 'center',
                                                alignItems: 'center',
                                            }}>
                                            {open ? sectionTitle?.toUpperCase() : sectionTitle?.charAt(0)}
                                        </ListSubheader>
                                    }>
                                    {section?.items?.map((item) => {
                                        const itemLabel = translate(item?.label);
                                        return (
                                            <ListItem
                                                key={`item-${itemLabel}`}
                                                disablePadding
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: open ? 'flex-start' : 'center',
                                                    alignItems: 'flex-start',
                                                    width: '100%',
                                                }}>
                                                <StyledTooltip title={itemLabel} placement="right" disableHoverListener={open}>
                                                    <ListItemButton
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            color: '#454545',
                                                            borderRadius: '4px',
                                                            '&.Mui-selected': {
                                                                backgroundColor: '#137dc540',
                                                                color: '#137DC5',
                                                                '&:hover': {
                                                                    backgroundColor: '#137dc560',
                                                                },
                                                            },
                                                        }}
                                                        onClick={() => history.push(item?.path)}
                                                        selected={getActualPath(item?.path)}>
                                                        <ListItemIcon sx={{ minWidth: 'auto' }}>{renderIcon({ src: item?.icon, color: getActualPath(item?.path) ? '#137DC5' : '#454545' })}</ListItemIcon>
                                                        {open && (
                                                            <ListItemText sx={{ marginLeft: '1em' }}>
                                                                <Typography sx={{ fontWeight: 'bold', color: 'inherit' }}>{itemLabel}</Typography>
                                                            </ListItemText>
                                                        )}
                                                    </ListItemButton>
                                                </StyledTooltip>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            );
                        })}
                    </Box>
                    <ListItem
                        sx={{
                            display: 'flex',
                            justifyContent: open ? 'flex-start' : 'center',
                            alignItems: 'flex-start',
                            marginTop: 'auto',
                            marginBottom: (theme) => theme.spacing(1),
                            width: '100%',
                            px: open ? '16px' : '8px',
                            py: 2,
                        }}
                        disablePadding>
                        <StyledTooltip title={translate('layout.appBar.language')} placement="right" disableHoverListener={open}>
                            <LanguageDropDown collapsed={!open} />
                        </StyledTooltip>
                    </ListItem>

                    <ListItem
                        sx={{
                            display: 'flex',
                            justifyContent: open ? 'flex-start' : 'center',
                            alignItems: 'flex-start',
                            marginTop: 'auto',
                            marginBottom: (theme) => theme.spacing(1),
                            px: open ? '16px' : '8px',
                            width: '100%',
                        }}
                        disablePadding>
                        <StyledTooltip title={translate('layout.appBar.currency')} placement="right" disableHoverListener={open}>
                            <MoneyDropDown collapsed={!open} />
                        </StyledTooltip>
                    </ListItem>

                    <ListItem
                        sx={{
                            display: 'flex',
                            justifyContent: open ? 'flex-start' : 'center',
                            alignItems: 'flex-start',
                            marginTop: 'auto',
                            marginBottom: (theme) => theme.spacing(1),
                            px: open ? '16px' : '8px',
                            width: '100%',
                        }}
                        disablePadding
                        onClick={onLogoutClick}>
                        <StyledTooltip title={translate('layout.menu.sign_out')} placement="right" disableHoverListener={open}>
                            <ListItemButton sx={{ display: 'flex', justifyContent: 'center' }}>
                                <ListItemIcon sx={{ minWidth: 'auto' }}>
                                    <ExitToApp sx={{ width: 24, height: 24, color: '#454545' }} />
                                </ListItemIcon>
                                {open && (
                                    <ListItemText sx={{ marginLeft: '1em' }}>
                                        <Typography sx={{ fontWeight: 'bold', color: '#454545' }}>{translate('layout.menu.sign_out')}</Typography>
                                    </ListItemText>
                                )}
                            </ListItemButton>
                        </StyledTooltip>
                    </ListItem>

                    {open && (
                        <Typography sx={{ padding: (theme) => theme.spacing(2) }}>
                            © Global Triper -<Link>{` ${translate('termsAndConditions.privacyPolicy')}`}</Link>
                        </Typography>
                    )}
                </Drawer>
            </Box>
        </>
    );
};

export default Sidebar;
